import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import moment from 'moment';
import { isNil } from 'lodash';

import { RootState } from 'app/store';
import { ApiResListsType, OhsApiRequestName } from 'global-services/api/OhsApiModels';
import globalModuleSearch from 'search/OhsSearchServices';
import {
  GlobalSearchState,
  OhsGlobalSearchFilter,
  OhsGlobalSearchPayload,
} from 'search/OhsSearchModels';
import { ModuleType, TierType } from 'global-services/constants/OhsObject';
import handleRegisterAsyncData, {
  handleRegisterCountAsyncData,
} from 'global-components/register/OhsModuleRegisterUtils';
import globalModuleSearchHistory from 'search/OhsSearchHistoryServices';
import OhsFetchAll from 'global-services/OhsFetchAll';
import { OhsUser } from 'user/OhsUser';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { getOhsLocalStorage, setOhsLocalStorage } from 'global-services/OhsDataParse';

import {
  AllocatedListType,
  OhsInspectionFilterPayload,
  OhsAvailableInspectionFilterPayload,
  OhsInspectionViewPresets,
  OhsInspectionRootState,
  OhsInspectionHistoryFilterPayload,
  OhsInspectionScheduledMain,
  OhsInspectionScheduleFilterPayload,
  OhsInspectionMain,
  OhsAvailableInspectionMain,
} from './OhsInspectionModels';
import OhsInspectionRecord from './models/OhsInspectionRecord';
import {
  getAvailableInspectionList,
  getAvailableInspectionListTask,
  getAvailableInspectionSearchList,
  getInspectionAllocatedList,
  getInspectionCopyList,
  getInspectionHistoryList,
  getInspectionList,
  getInspectionListWithAllocations,
  getInspectionScheduleList,
  getInspectionScheduleSearchLists,
  getInspectionSearchLists,
} from './OhsInspectionServices';
import parseListAllocations from './register/utils/OhsParseListAllocations';

enum RPCTypes {
  List = 'list',
  Scheduled = 'scheduled',
  History = 'history',
}

export interface InspectionListRecordState {
  isLoading: boolean;
  inspectionList: ApiResListsType<any[]> | null;
  inspectionScheduleList: ApiResListsType<any[]> | null;
  inspectionHistoryList: ApiResListsType<any[]> | null;
  allocatedList: AllocatedListType[] | null;
  inspectionCopyList: {
    isLoading: boolean;
    copyList: OhsInspectionRecord[];
  };
  availableInspectionList: ApiResListsType<any[]> | null;
  currentPage: number;
  currentViewPreset: OhsInspectionViewPresets;
  availableInspectionSearch: GlobalSearchState;
}

export const defaultInspectionAvailableSearch: OhsGlobalSearchFilter = {
  page: 1,
  count: false,
  modules: [],
  skipOrgRecords: false,
  workplaces: [],
  task: {
    complete: false,
    includeMasterRecords: false,
  },
  skipAllocatedRecords: false,
  archived: false,
};

const initialInspectionSearchPayload: OhsGlobalSearchPayload = {
  searchKey: '',
  substringSearch: true,
  highlight: false,
  filter: defaultInspectionAvailableSearch,
};

const initSearchResults = {
  items: [],
  pagination: {
    page: 1,
    totalPages: 1,
    next: '',
  },
};

const initialState: InspectionListRecordState = {
  isLoading: false,
  inspectionList: null,
  inspectionScheduleList: null,
  inspectionHistoryList: null,
  allocatedList: null,
  inspectionCopyList: {
    isLoading: false,
    copyList: [],
  },
  availableInspectionList: null,
  availableInspectionSearch: {
    isLoading: false,
    searchInfo: initialInspectionSearchPayload,
    searchResults: initSearchResults,
    currentPage: 1,
  },
  currentPage: 1,
  currentViewPreset: OhsInspectionViewPresets.view2InspectionScheduledList,
};

export const fetchInspectionAllocatedListAsync = createAsyncThunk<
  AllocatedListType[],
  { _ids: string[] },
  { state: RootState }
>('inspection/fetchInspectionAllocatedList', async (inspectionIDs, thunkAPI): Promise<any> => {
  try {
    const response = await getInspectionAllocatedList(inspectionIDs._ids);
    const resVal = response;
    return resVal;
  } catch (err: any) {
    const error: AxiosError<any> = err; // cast the error for access
    if (!error.response) {
      throw err;
    }

    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const inspectionSearch = async (
  searchDetails: any,
  apiRequestName?: OhsApiRequestName
): Promise<ApiResListsType<OhsInspectionRecord[]> | null> => {
  const globalSearchRes: any = await globalModuleSearch(searchDetails, apiRequestName);
  return globalSearchRes.result;
};

export const inspectionSearchHistory = async (
  searchDetails: any
): Promise<ApiResListsType<OhsInspectionRecord[]> | null> => {
  const globalSearchRes: any = await globalModuleSearchHistory(searchDetails);
  return globalSearchRes.result;
};

const inspectionListRPC = async ({
  inspectionState,
  inspectionFilters,
  type,
  count,
  orgAdminTierId,
  inspectionId,
}: {
  inspectionState: OhsInspectionRootState;
  inspectionFilters: OhsInspectionFilterPayload;
  type: RPCTypes;
  count: boolean;
  orgAdminTierId?: string;
  inspectionId?: string;
}): Promise<ApiResListsType<any[]> | null> => {
  const { globalSearch } = inspectionState;
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;
  const hasGlobalSearch = inspectionState.globalSearch.searchInfo.searchKey !== '';

  const getSearchFilter = () => {
    const { filter } = globalSearch.searchInfo;
    if (type === RPCTypes.Scheduled) {
      return {
        ...globalSearch.searchInfo,
        filter: {
          skipOrgRecords: filter.skipOrgRecords,
          workplaces: filter.workplaces,
          page: filter.page,
          count,
          archived: filter.archived,
        },
      };
    }
    if (type === RPCTypes.List && user?.tier?.type === TierType.T3) {
      return {
        ...globalSearch.searchInfo,
        filter: { ...filter, count, skipAllocatedRecords: true },
      };
    }
    if (type === RPCTypes.History) {
      const payload = {
        ...globalSearch.searchInfo,
        filter: {
          _id: inspectionId,
          type: ModuleType.Inspection,
          page: filter.page,
          count,
          workplaces: filter.workplaces,
          skipOrgRecords: filter.skipOrgRecords,
        },
        sort: globalSearch.searchInfo.sort && JSON.parse(globalSearch.searchInfo.sort),
      };
      if (!globalSearch.searchInfo.sort) {
        delete payload.sort;
      }

      return payload;
    }

    return { ...globalSearch.searchInfo, filter: { ...filter, count } };
  };

  const withViewPermission = user?.configs.inspection?.PERMISSIONS.view;

  if (userTier) {
    if (userTier === 1 && orgAdminTierId) {
      const response = getInspectionList(inspectionFilters, orgAdminTierId);
      return response;
    }

    if (withViewPermission) {
      if (type === RPCTypes.History) {
        return hasGlobalSearch
          ? inspectionSearchHistory(getSearchFilter())
          : getInspectionHistoryList(inspectionFilters, inspectionId || '');
      }
      if (userTier === 2) {
        return hasGlobalSearch
          ? inspectionSearch(getSearchFilter())
          : getInspectionList(inspectionFilters);
      }
      if (userTier === 3) {
        return getInspectionListWithAllocations(inspectionState, inspectionFilters, () =>
          inspectionSearch(getSearchFilter())
        );
      }
      if (userTier === 4) {
        return hasGlobalSearch
          ? inspectionSearch(getSearchFilter())
          : getInspectionList(inspectionFilters);
      }
    }
  }
  return null;
};

// TEMPLATE
const inspectionListsRPC = async (
  payload: OhsInspectionFilterPayload | OhsInspectionMain,
  hasSearch: boolean = false,
  orgAdminTierId: string = ''
): Promise<any> => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;

  const response = hasSearch
    ? await getInspectionSearchLists(payload as OhsInspectionMain)
    : await getInspectionList(payload as OhsInspectionFilterPayload, orgAdminTierId);

  // Tier 3: use allocations logic
  if (userTier === 3 && response?.items?.length > 0) {
    const getIDs = response.items.map((item: OhsInspectionRecord) => item._id);
    const allocatedList = await getInspectionAllocatedList(getIDs);
    return parseListAllocations(response, allocatedList) as ApiResListsType<
      OhsInspectionRecord[]
    > | null;
  }

  // For all other cases, use without allocations
  return response;
};

export const fetchInspectionListAsync = createAsyncThunk<
  {
    list: ApiResListsType<OhsInspectionRecord[]> | null;
    count: ApiResListsType<OhsInspectionRecord[]> | null;
  },
  {
    list: OhsInspectionFilterPayload | OhsInspectionMain;
    count: OhsInspectionFilterPayload | OhsInspectionMain;
    hasSearch: boolean;
    orgAdminTierId?: string;
  },
  { state: RootState }
>('inspection/fetchInspectionListAsync', async (payload, thunkAPI) => {
  try {
    const orgAdminTierId = payload.orgAdminTierId || '';
    const [list, count] = await Promise.all([
      payload.hasSearch
        ? inspectionListsRPC(payload.list as OhsInspectionMain, true, orgAdminTierId)
        : inspectionListsRPC(payload.list as OhsInspectionFilterPayload, false, orgAdminTierId),
      payload.hasSearch
        ? inspectionListsRPC(payload.count as OhsInspectionMain, true, orgAdminTierId)
        : getInspectionList(payload.count as OhsInspectionFilterPayload, orgAdminTierId),
    ]);

    return { list, count };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// SCHEDULE
const inspectionScheduledListsRPC = async (
  payload: OhsInspectionScheduleFilterPayload | OhsInspectionScheduledMain,
  hasSearch: boolean = false
): Promise<any> => {
  const response = hasSearch
    ? await getInspectionScheduleSearchLists(payload as OhsInspectionScheduledMain)
    : await getInspectionScheduleList(payload as OhsInspectionScheduleFilterPayload);
  return response;
};

export const fetchInspectionScheduledListAsync = createAsyncThunk<
  {
    list: ApiResListsType<OhsInspectionRecord[]> | null;
    count: ApiResListsType<OhsInspectionRecord[]> | null;
  },
  {
    list: OhsInspectionScheduleFilterPayload | OhsInspectionScheduledMain;
    count: OhsInspectionScheduleFilterPayload | OhsInspectionScheduledMain;
    hasSearch: boolean;
  },
  { state: RootState }
>('inspection/fetchInspectionScheduledListAsync', async (payload, thunkAPI) => {
  try {
    const [list, count] = await Promise.all([
      payload.hasSearch
        ? inspectionScheduledListsRPC(payload.list as OhsInspectionScheduledMain, true)
        : inspectionScheduledListsRPC(payload.list as OhsInspectionScheduleFilterPayload),
      payload.hasSearch
        ? inspectionScheduledListsRPC(payload.count as OhsInspectionScheduledMain, true)
        : getInspectionScheduleList(payload.count as OhsInspectionScheduleFilterPayload),
    ]);

    return { list, count };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// HISTORY
export const fetchInspectionHistoryListAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  string
>('inspection/fetchInspectionHistoryList', async (inspectionId, thunkAPI) => {
  const {
    inspection,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsInspectionRootState;
  const inspectionState = thunkAPI.getState() as OhsInspectionRootState;

  const data = inspection.inspectionLists;
  const { currentPage, currentViewPreset } = inspection;
  const viewPresetFilter = filterInfo.inspectionModule[currentViewPreset];

  const inspectionFilters: OhsInspectionHistoryFilterPayload = {
    ...viewPresetFilter,
    dateCompleted: filterInfo.inspectionModule.dateCompleted
      ? {
          start: moment(new Date(filterInfo.inspectionModule.dateCompleted.start)).format(
            'YYYY-MM-DD'
          ),
          end: moment(new Date(filterInfo.inspectionModule.dateCompleted.end)).format('YYYY-MM-DD'),
        }
      : undefined,
    next: currentPage > 10 ? data?.pagination?.next ?? '' : '',
    page: currentPage > 10 ? 0 : currentPage,
    sort: { ...JSON.parse(String(filterInfo.inspectionModule[currentViewPreset].sort)) },
    count: false,
  };

  try {
    const response = await inspectionListRPC({
      inspectionState,
      inspectionFilters,
      type: RPCTypes.History,
      count: false,
      inspectionId,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
export const fetchInspectionHistoryListCountAsync = createAsyncThunk<
  ApiResListsType<OhsInspectionRecord[]> | null,
  string
>('inspection/fetchInspectionHistoryCountList', async (inspectionId, thunkAPI) => {
  const {
    inspection,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsInspectionRootState;

  const inspectionState = thunkAPI.getState() as OhsInspectionRootState;
  const { currentViewPreset } = inspection;
  const viewPresetFilter = filterInfo.inspectionModule[currentViewPreset];

  const inspectionFilters: OhsInspectionHistoryFilterPayload = {
    ...viewPresetFilter,
    dateCompleted: filterInfo.inspectionModule.dateCompleted
      ? {
          start: moment(new Date(filterInfo.inspectionModule.dateCompleted.start)).format(
            'YYYY-MM-DD'
          ),
          end: moment(new Date(filterInfo.inspectionModule.dateCompleted.end)).format('YYYY-MM-DD'),
        }
      : undefined,
    count: true,
    page: 1,
    sort: { ...JSON.parse(String(filterInfo.inspectionModule[currentViewPreset].sort)) },
  };

  try {
    const response = await inspectionListRPC({
      inspectionState,
      inspectionFilters,
      type: RPCTypes.History,
      count: true,
      inspectionId,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// COPY
export const fetchInspectionCopyListAsync = createAsyncThunk<
  ApiResListsType<any[]>,
  undefined,
  { state: RootState }
>(
  'inspection/fetchInspectionCopyList',
  async (inspectionIDs, thunkAPI): Promise<ApiResListsType<any[]> | any> => {
    try {
      const response = await OhsFetchAll(getInspectionCopyList, {
        archived: false,
        sort: {
          order: 1,
          key: '_id',
        },
      });
      const resVal = response;
      return resVal;
    } catch (err: any) {
      const error: AxiosError<any> = err; // cast the error for access
      if (!error.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

// AVAILABLE
const availableInspectionListsRPC = async (
  payload: OhsAvailableInspectionFilterPayload | OhsAvailableInspectionMain,
  hasSearch: boolean = false
): Promise<any> => {
  const response = hasSearch
    ? await getAvailableInspectionSearchList(payload as OhsAvailableInspectionMain)
    : await getAvailableInspectionList(payload as OhsAvailableInspectionFilterPayload);
  return response;
};

export const fetchAvailableInspectionListAsync = createAsyncThunk<
  {
    list: ApiResListsType<OhsInspectionRecord[]> | null;
    count: ApiResListsType<OhsInspectionRecord[]> | null;
  },
  {
    list: OhsAvailableInspectionFilterPayload | OhsAvailableInspectionMain;
    count: OhsAvailableInspectionFilterPayload | OhsAvailableInspectionMain;
    hasSearch: boolean;
  },
  { state: RootState }
>('inspection/fetchAvailableInspectionListAsync', async (payload, thunkAPI) => {
  try {
    // eslint-disable-next-line prefer-const
    let [list, count] = await Promise.all([
      payload.hasSearch
        ? availableInspectionListsRPC(payload.list as OhsAvailableInspectionMain, true)
        : availableInspectionListsRPC(payload.list as OhsAvailableInspectionFilterPayload),
      payload.hasSearch
        ? availableInspectionListsRPC(payload.count as OhsAvailableInspectionMain, true)
        : getAvailableInspectionList(payload.count as OhsAvailableInspectionFilterPayload),
    ]);

    try {
      // call separate api to include inexecution prop in inspection record
      const recurringInspections = (list?.items || []).filter(
        (item: any) => !isNil(item.frequencyKey)
      );
      const recurringIds = recurringInspections.map((item: any) => item._id);
      const taskList = await getAvailableInspectionListTask(recurringIds);

      const taskObj: any = {};
      (taskList || []).forEach((task) => {
        taskObj[task.for._id] = task;
      });

      const formattedItems = (list?.items || []).map((item: any) => {
        const task = taskObj[item._id];
        if (task) {
          return { ...item, execute: { task } };
        }
        return item;
      });

      list = { ...list, items: formattedItems };
    } catch (error) {
      console.error(error);
    }
    return { list, count };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const inspectionSlice = createSlice({
  name: 'inspection',
  initialState,
  reducers: {
    setCurrentRegisterPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setInspectionIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setViewPreset: (state, action: PayloadAction<OhsInspectionViewPresets>) => {
      state.currentViewPreset = action.payload;
    },
    setAvailableInspectionSearch: (state, action: PayloadAction<OhsGlobalSearchPayload>) => {
      state.availableInspectionSearch.searchInfo = action.payload;
    },
    clearAvailableInspectionSearch: (state) => {
      state.availableInspectionSearch.searchInfo =
        initialState.availableInspectionSearch.searchInfo;
    },
    setAvailableInspectionSearchCurrentPage: (state, action: PayloadAction<number>) => {
      state.availableInspectionSearch.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // TEMPLATE
      .addCase(fetchInspectionListAsync.pending, (state) => {
        state.isLoading = true;
        state.inspectionList = {
          items: [],
          pagination: state.inspectionList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchInspectionListAsync.fulfilled, (state, action) => {
        if (action.payload.list) {
          state.inspectionList = action.payload.list;
          if (state?.inspectionList?.pagination?.totalPages) {
            setOhsLocalStorage(
              'inspectionListTotalPages',
              state.inspectionList.pagination.totalPages
            );
          }
          let setItemsLastId = '';
          const inspectionItems = [...(state.inspectionList?.items || [])];
          if (inspectionItems.length > 0) setItemsLastId = inspectionItems.pop()._id;

          state.inspectionList.pagination.next = setItemsLastId;
          state.inspectionList.pagination.totalPages =
            getOhsLocalStorage('inspectionListTotalPages') ?? 0;
          state.isLoading = false;
        }
        if (action.payload.count && state.inspectionList) {
          setOhsLocalStorage(
            'inspectionListTotalPages',
            action.payload.count.pagination.totalPages
          );
          state.inspectionList.pagination.totalPages =
            action.payload.count.pagination.totalPages ?? 0;
        }
      })
      .addCase(fetchInspectionListAsync.rejected, (state) => {
        state.isLoading = false;
        state.inspectionList = null;
      })

      // SCHEDULE
      .addCase(fetchInspectionScheduledListAsync.pending, (state) => {
        state.isLoading = true;
        state.inspectionScheduleList = {
          items: [],
          pagination: state.inspectionScheduleList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchInspectionScheduledListAsync.fulfilled, (state, action) => {
        if (action.payload.list) {
          state.inspectionScheduleList = action.payload.list;
          if (state?.inspectionScheduleList?.pagination?.totalPages) {
            setOhsLocalStorage(
              'inspectionScheduledListTotalPages',
              state.inspectionScheduleList.pagination.totalPages
            );
          }
          let setItemsLastId = '';
          const inspectionScheduledItems = [...(state.inspectionScheduleList?.items || [])];
          if (inspectionScheduledItems.length > 0)
            setItemsLastId = inspectionScheduledItems.pop()._id;

          state.inspectionScheduleList.pagination.next = setItemsLastId;
          state.inspectionScheduleList.pagination.totalPages =
            getOhsLocalStorage('inspectionScheduledListTotalPages') ?? 0;
          state.isLoading = false;
        }
        if (action.payload.count && state.inspectionScheduleList) {
          setOhsLocalStorage(
            'inspectionScheduledListTotalPages',
            action.payload.count.pagination.totalPages
          );
          state.inspectionScheduleList.pagination.totalPages =
            action.payload.count.pagination.totalPages ?? 0;
        }
      })
      .addCase(fetchInspectionScheduledListAsync.rejected, (state) => {
        state.isLoading = false;
        state.inspectionScheduleList = null;
      })

      // HISTORY
      .addCase(fetchInspectionHistoryListAsync.pending, (state) => {
        state.isLoading = true;
        state.inspectionHistoryList = {
          items: [],
          pagination: state.inspectionHistoryList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchInspectionHistoryListAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const inspectionScheduleResult = action.payload;
          const inspectionHistoryList = handleRegisterAsyncData(
            inspectionScheduleResult,
            'inspectionHistoryListTotalPages'
          );
          state.isLoading = false;
          state.inspectionHistoryList = inspectionHistoryList;
        }
      })
      .addCase(fetchInspectionHistoryListAsync.rejected, (state) => {
        state.inspectionHistoryList = null;
      })
      .addCase(fetchInspectionHistoryListCountAsync.fulfilled, (state, action) => {
        if (state.inspectionHistoryList && action.payload) {
          const countInspectionHistoryList = handleRegisterCountAsyncData(
            action.payload,
            state.inspectionHistoryList,
            'inspectionHistoryListTotalPages'
          );
          state.inspectionHistoryList = countInspectionHistoryList;
          state.isLoading = false;
        }
      })

      // ALLOCATED
      .addCase(fetchInspectionAllocatedListAsync.pending, (state) => {
        state.allocatedList = [];
      })
      .addCase(fetchInspectionAllocatedListAsync.fulfilled, (state, action) => {
        state.allocatedList = action.payload ?? [];
        state.isLoading = false;
      })

      // COPY
      .addCase(fetchInspectionCopyListAsync.pending, (state) => {
        state.inspectionCopyList.copyList = [];
        state.inspectionCopyList.isLoading = true;
      })
      .addCase(fetchInspectionCopyListAsync.fulfilled, (state, action) => {
        state.inspectionCopyList.copyList = action.payload.items ?? [];
        state.inspectionCopyList.isLoading = false;
      })

      // AVAILABLE
      .addCase(fetchAvailableInspectionListAsync.pending, (state) => {
        state.isLoading = true;
        state.availableInspectionList = {
          items: [],
          pagination: state.availableInspectionList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchAvailableInspectionListAsync.fulfilled, (state, action) => {
        if (action.payload.list) {
          state.availableInspectionList = action.payload.list;
          if (state?.availableInspectionList?.pagination?.totalPages) {
            setOhsLocalStorage(
              'availableInspectionListTotalPages',
              state.availableInspectionList.pagination.totalPages
            );
          }
          let setItemsLastId = '';
          const availableInspectionItems = [...(state.availableInspectionList?.items || [])];
          if (availableInspectionItems.length > 0)
            setItemsLastId = availableInspectionItems.pop()._id;

          state.availableInspectionList.pagination.next = setItemsLastId;
          state.availableInspectionList.pagination.totalPages =
            getOhsLocalStorage('availableInspectionListTotalPages') ?? 0;
          state.isLoading = false;
        }
        if (action.payload.count && state.availableInspectionList) {
          setOhsLocalStorage(
            'availableInspectionListTotalPages',
            action.payload.count.pagination.totalPages
          );
          state.availableInspectionList.pagination.totalPages =
            action.payload.count.pagination.totalPages ?? 0;
        }
      })
      .addCase(fetchAvailableInspectionListAsync.rejected, (state) => {
        state.availableInspectionList = null;
      });
  },
});
// Memoized Selectors
const inspectionState = (state: RootState) => state.inspection;
export const getOhsInspectionList = createSelector([inspectionState], (inspection) => inspection);
export const getAvailableInspectionSearch = createSelector(
  [inspectionState],
  (inspection) => inspection.availableInspectionSearch
);

export const {
  setCurrentRegisterPage,
  setInspectionIsLoading,
  setViewPreset,
  setAvailableInspectionSearch,
  clearAvailableInspectionSearch,
  setAvailableInspectionSearchCurrentPage,
} = inspectionSlice.actions;
export const inspectionModuleReducer = inspectionSlice.reducer;
